import React, { useState, useEffect, useRef } from 'react';
import { 
    CheckCircle, AlertTriangle, LogOut, User, Bed, DollarSign, ArrowLeft, Printer, Clock, Percent, ChevronDown, ChevronUp, Utensils, ChevronRight, ShieldAlert, RefreshCw, ShoppingBag, Building2 
} from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';
import FacturePrint from './factureprint';
import '../Dashboard.css';

const roundFCFA = (val) => Math.round((Number(val) || 0) * 100) / 100;

const ChekoutFacturation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const reservationId = searchParams.get('reservation_id');
  const ligneId = searchParams.get('ligne_id');
  const mode = searchParams.get('mode');

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const company_id = user?.company_id || user?.companyId;

  const [loading, setLoading] = useState(false);
  const [detailsSejour, setDetailsSejour] = useState(null);
  const [societeDetails, setSocieteDetails] = useState(null);
  const [companyInfo, setCompanyInfo] = useState({ regime_tva_recuperable: 1, taux_tva: 18.0 });
  
  const [methodesPaiement, setMethodesPaiement] = useState([]);
  
  const [paymentMethodHeboId, setPaymentMethodHeboId] = useState('');
  const [montantHeboVerse, setMontantHeboVerse] = useState('');
  const [referenceHebo, setReferenceHebo] = useState('');

  const [paymentMethodRestoId, setPaymentMethodRestoId] = useState('');
  const [montantRestoVerse, setMontantRestoVerse] = useState('');
  const [referenceResto, setReferenceResto] = useState('');

  const [paymentMethodExtraId, setPaymentMethodExtraId] = useState('');
  const [montantExtraVerse, setMontantExtraVerse] = useState('');
  const [referenceExtra, setReferenceExtra] = useState('');
  
  const [isLateCheckout, setIsLateCheckout] = useState(false);
  const [lateForm, setLateForm] = useState({
    heure_depart_effective: '16:00',
    regle_tarif_id: ''
  });
  const [reglesTarifs, setReglesTarifs] = useState([]);

  const dateDuJourStr = new Date().toISOString().split('T')[0];
  const [nouvelleDateDepart, setNouvelleDateDepart] = useState(dateDuJourStr);

  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Facture_${detailsSejour?.facture_no || reservationId || 'Hebergement'}`,
  });

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const fetchCheckoutDetails = async () => {
    try {
      setLoading(true);
      const endpoint = reservationId 
        ? `/hebergement/reservations/${reservationId}` 
        : `/hebergement/reservations/lignes-chambres/${ligneId}`;
 
      const [resData, reglesData, methodesData, compRes] = await Promise.all([
        API.get(endpoint),
        API.get('/hebergement/regles-tarifs').catch(() => ({ data: [] })),
        API.get('/plan-comptable/paiements/methodes').catch(() => ({ data: { data: [] } })),
        company_id ? API.get(`/company/${company_id}`).catch(() => ({ data: {} })) : Promise.resolve({ data: {} })
      ]);

      if (compRes.data?.success && compRes.data.data) {
        setCompanyInfo({
          regime_tva_recuperable: compRes.data.data.regime_tva_recuperable ?? 1,
          taux_tva: compRes.data.data.taux_tva ?? 18.0,
          name: compRes.data.data.name || compRes.data.data.nom,
          address: compRes.data.data.address || compRes.data.data.adresse,
          phone: compRes.data.data.phone || compRes.data.data.telephone,
          email: compRes.data.data.email,
          logo_data: compRes.data.data.logo_data || compRes.data.data.logo || compRes.data.data.logo_url
        });
      }

      const data = resData.data;
      setDetailsSejour(data);
      setReglesTarifs(reglesData.data || []);

      if (data?.societe) {
        setSocieteDetails(data.societe);
      } else if (data?.societe_id) {
        API.get('/hebergement/societes')
          .then(socListRes => {
            const list = socListRes.data?.data || socListRes.data || [];
            const found = list.find(s => String(s.id) === String(data.societe_id) || String(s.code) === String(data.societe_id));
            setSocieteDetails(found || null);
          })
          .catch(() => setSocieteDetails(null));
      } else {
        setSocieteDetails(null);
      }
      if (data?.date_depart_prevue) {
        setNouvelleDateDepart(mode === 'depart_anticipe' ? dateDuJourStr : data.date_depart_prevue.split('T')[0]);
      }

      const methodesActives = (methodesData.data?.data || []).filter(m => m.is_active !== 0);
      setMethodesPaiement(methodesActives);
      if (methodesActives.length > 0) {
        if (!paymentMethodHeboId) setPaymentMethodHeboId(methodesActives[0].id);
        if (!paymentMethodRestoId) setPaymentMethodRestoId(methodesActives[0].id);
        if (!paymentMethodExtraId) setPaymentMethodExtraId(methodesActives[0].id);
      }
    } catch (err) {
      console.error("Erreur chargement facture:", err);
      showToast("Impossible de charger les détails du séjour", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (reservationId || ligneId) {
      fetchCheckoutDetails();
    }
  }, [reservationId, ligneId]);

  const isEntrepriseSousTva = companyInfo.regime_tva_recuperable === 1 && Number(companyInfo.taux_tva || 0) > 0;

  const dateArriveeStr = detailsSejour?.date_arrivee_prevue || detailsSejour?.date_arrivee;
  const dateDepartStr = detailsSejour?.date_depart_prevue;

  let nombreNuitesPrevues = 1;
  if (dateArriveeStr && dateDepartStr) {
    const dArr = new Date(dateArriveeStr.split('T')[0]);
    const dDep = new Date(dateDepartStr.split('T')[0]);
    const diffTime = dDep - dArr;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 0) nombreNuitesPrevues = diffDays;
  }

  const dateArriveeClean = dateArriveeStr ? dateArriveeStr.split('T')[0] : dateDuJourStr;

  let nombreNuitesReelles = nombreNuitesPrevues;
  if (mode === 'facture' || mode === 'facture_groupe') {
    nombreNuitesReelles = nombreNuitesPrevues;
  } else if (mode === 'depart_anticipe') {
    const dateDepartCibleClean = nouvelleDateDepart || dateDuJourStr;
    if (dateArriveeClean === dateDepartCibleClean) {
      nombreNuitesReelles = 1;
    } else {
      const dArr = new Date(dateArriveeClean);
      const dDep = new Date(dateDepartCibleClean);
      const diffDays = Math.ceil((dDep - dArr) / (1000 * 60 * 60 * 24));
      nombreNuitesReelles = Math.max(1, diffDays);
    }
  }

  // Extraction robuste de la civilité et du nom complet
  const clientObjDetails = detailsSejour?.client || {};
  const civiliteDetectee = clientObjDetails.civilite_libelle || clientObjDetails.civilite || detailsSejour?.civilite_libelle || detailsSejour?.civilite || '';
  const nomBrutClient = detailsSejour?.client_principal_nom || detailsSejour?.client_nom || detailsSejour?.reservant_nom || clientObjDetails.nom || 'Client';
  const clientNom = [civiliteDetectee, nomBrutClient].filter(Boolean).join(' ');

  const customerId = detailsSejour?.customer_id || detailsSejour?.client_id || clientObjDetails.id || null;

  const rawLignes = detailsSejour?.chambres || detailsSejour?.lignes || detailsSejour?.lignes_chambres || [];
  
  const estDejaCheckout = Boolean(
    detailsSejour?.statut === 'CHECK_OUT' || 
    detailsSejour?.toutes_lignes_checkout === true ||
    rawLignes.some(l => String(l.statut || '').toUpperCase() === 'CHECK_OUT')
  );

  let lignesActives = rawLignes.length > 0 
    ? rawLignes.filter(l => {
        const st = (l.statut || l.status || '').toUpperCase();
        const isCheckedIn = l.is_checkin === 1 || l.is_checkin === true || l.checked_in === true;
        if (st) {
          return st === 'CHECK_IN' || st === 'CHECKIN' || st === 'OCCUPE' || st === 'EN_COURS' || st === 'ACTIVE' || isCheckedIn;
        }
        return isCheckedIn || true;
      })
    : [];

  if (lignesActives.length === 0) {
    if (rawLignes.length > 0) {
      lignesActives = rawLignes;
    } else {
      lignesActives = [{
        id: detailsSejour?.id || reservationId || ligneId,
        client_nom: clientNom,
        chambre_numero: detailsSejour?.chambre_numero,
        nombre_nuites: nombreNuitesReelles,
        montant_ligne: detailsSejour?.montant_ligne || detailsSejour?.montant_total || 0,
        prix_unitaire: detailsSejour?.prix_unitaire || detailsSejour?.tarif_nuit,
        soumis_tva: detailsSejour?.soumis_tva,
        taux_tva: detailsSejour?.taux_tva
      }];
    }
  }

  const lignesTarifiees = lignesActives.map(ligne => {
    const prixUnit = Number(ligne.prix_unitaire || ligne.tarif_nuit || ligne.montant_ligne || ligne.montant || 0);
    const nuitesLigne = Number(ligne.nombre_nuites || nombreNuitesReelles);
    const montantLigneTTC = ligne.montant_ligne !== undefined ? Number(ligne.montant_ligne) : (prixUnit > 0 ? (prixUnit * nuitesLigne) : Number(ligne.montant_ttc || ligne.montant || 0));

    return {
      ...ligne,
      nuites: nuitesLigne,
      prix_unitaire: prixUnit > 0 ? prixUnit : (nuitesLigne > 0 ? montantLigneTTC / nuitesLigne : montantLigneTTC),
      montant_ttc: montantLigneTTC,
      occupant_nom: [civiliteDetectee, (ligne.client_nom || ligne.nom_client || ligne.occupant_nom || nomBrutClient)].filter(Boolean).join(' '),
      chambre: ligne.chambre_numero || ligne.numero_chambre || detailsSejour?.chambre_numero || 'N/A'
    };
  });

  const montantNuitees = lignesTarifiees.reduce((sum, l) => sum + l.montant_ttc, 0);
  const montantEarly = detailsSejour?.is_early_checkin ? Number(detailsSejour?.montant_early || 0) : 0;
  const totalNuitesGroupe = lignesTarifiees.reduce((sum, l) => sum + Number(l.nuites || 0), 0) || (nombreNuitesReelles * lignesTarifiees.length);

  const facturesRestoPostees = detailsSejour?.factures_postees || detailsSejour?.consommations_postees || [];
  const montantTotalResto = facturesRestoPostees.reduce((sum, f) => sum + Number(f.montant_total || f.montant || 0), 0);
  const totalPaiementsResto = facturesRestoPostees.reduce((sum, f) => sum + Number(f.montant_paye || 0), 0);
  const resteAPayerResto = roundFCFA(facturesRestoPostees.reduce((sum, f) => sum + Number(f.reste_a_payer !== undefined ? f.reste_a_payer : (Number(f.montant_total || 0) - Number(f.montant_paye || 0))), 0));
  const hasResto = facturesRestoPostees.length > 0;

  const consommationsExtras = detailsSejour?.consommations_extras || [];
  const montantTotalExtras = consommationsExtras.reduce((sum, e) => sum + Number(e.montant_ttc || e.montant || 0), 0);
  const totalPaiementsExtras = consommationsExtras.reduce((sum, e) => sum + Number(e.montant_paye || 0), 0);
  const resteAPayerExtras = roundFCFA(consommationsExtras.reduce((sum, e) => sum + Number(e.reste_a_payer !== undefined ? e.reste_a_payer : (Number(e.montant_ttc || 0) - Number(e.montant_paye || 0))), 0));
  const hasExtras = consommationsExtras.length > 0;

  let montantLate = 0;
  let lateRegleTva = false;
  let lateTauxTva = companyInfo.taux_tva;

  if (isLateCheckout && lateForm.regle_tarif_id) {
    const regleTrouvee = reglesTarifs.find(r => r.id === lateForm.regle_tarif_id);
    if (regleTrouvee) {
      const valeur = parseFloat(regleTrouvee.montant) || 0;
      if ((regleTrouvee.mode_valeur || 'FIXE').toUpperCase() === 'POURCENTAGE') {
        montantLate = (montantNuitees * valeur) / 100;
      } else {
        montantLate = valeur;
      }
      lateRegleTva = isEntrepriseSousTva && (regleTrouvee.soumis_tva === 1 || regleTrouvee.soumis_tva === true);
      lateTauxTva = Number(regleTrouvee.taux_tva || companyInfo.taux_tva);
    }
  }

  let totalHT = 0;
  let totalTVA = 0;

  lignesTarifiees.forEach(ligne => {
    const montantTTCItem = ligne.montant_ttc;
    const itemSoumisTva = isEntrepriseSousTva && (ligne.soumis_tva === 1 || ligne.soumis_tva === true || detailsSejour?.soumis_tva === 1);
    const itemTaux = Number(ligne.taux_tva || detailsSejour?.taux_tva || companyInfo.taux_tva);

    if (itemSoumisTva && itemTaux > 0) {
      const ht = montantTTCItem / (1 + (itemTaux / 100));
      totalHT += ht;
      totalTVA += (montantTTCItem - ht);
    } else {
      totalHT += montantTTCItem;
    }
  });

  if (montantEarly > 0) {
    const earlySoumisTva = isEntrepriseSousTva && (detailsSejour?.early_soumis_tva === 1 || detailsSejour?.soumis_tva === 1);
    if (earlySoumisTva) {
      const htEarly = montantEarly / (1 + (companyInfo.taux_tva / 100));
      totalHT += htEarly;
      totalTVA += (montantEarly - htEarly);
    } else {
      totalHT += montantEarly;
    }
  }

  if (montantLate > 0) {
    if (lateRegleTva && lateTauxTva > 0) {
      const htLate = montantLate / (1 + (lateTauxTva / 100));
      totalHT += htLate;
      totalTVA += (montantLate - htLate);
    } else {
      totalHT += montantLate;
    }
  }

  facturesRestoPostees.forEach(f => {
    const restoTTC = Number(f.montant_total || f.montant || 0);
    const restoHT = Number(f.montant_ht || 0);
    const restoTVA = Number(f.montant_tva || 0);
    const restoSoumisTva = isEntrepriseSousTva && (f.soumis_tva === 1 || f.soumis_tva === true);

    if (restoHT > 0 && restoSoumisTva) {
      totalHT += restoHT;
      totalTVA += restoTVA;
    } else {
      totalHT += restoTTC;
    }
  });

  consommationsExtras.forEach(e => {
    const extraTTC = Number(e.montant_ttc || e.montant || 0);
    const extraHT = Number(e.montant_ht || 0);
    const extraTVA = Number(e.montant_tva || 0);
    const extraSoumisTva = isEntrepriseSousTva && (e.soumis_tva === 1 || e.soumis_tva === true);

    if (extraHT > 0 && extraSoumisTva) {
      totalHT += extraHT;
      totalTVA += extraTVA;
    } else {
      totalHT += extraTTC;
    }
  });

  const montantTotalGlobal = Number(detailsSejour?.montant_ttc || (montantNuitees + montantEarly + montantLate + montantTotalResto + montantTotalExtras));
  const montantTotalHebbergement = montantNuitees + montantEarly + montantLate;

  const paiementsAnterieurs = detailsSejour?.paiements || detailsSejour?.acomptes || detailsSejour?.reglements || [];
   
  const totalPaiementsHebo = detailsSejour?.montant_paye !== undefined 
    ? Number(detailsSejour.montant_paye) 
    : paiementsAnterieurs
        .filter(p => Number(p.montant_hebergement || 0) > 0 || (Number(p.montant || 0) > 0 && Number(p.montant_restauration || 0) === 0 && Number(p.montant_services_extras || 0) === 0))
        .reduce((sum, p) => sum + Number(p.montant_hebergement || p.montant || 0), 0);

  const resteAPayerHebo = roundFCFA(Math.max(0, montantTotalHebbergement - totalPaiementsHebo));
  const totalPayeHeboGlobal = roundFCFA(lignesTarifiees.reduce((sum, l) => sum + Number(l.montant_paye || 0), 0) || totalPaiementsHebo);

  const isMethodNotCash = (methodId) => {
    const methodObj = methodesPaiement.find(m => m.id === methodId);
    if (!methodObj) return false;
    const icone = (methodObj.icone_name || '').toLowerCase();
    const lib = (methodObj.libelle || '').toLowerCase();
    const isEsp = /(espece|cash|esp|wallet)/i.test(icone) || /(espece|cash|esp)/i.test(lib);
    return !isEsp;
  };

  const isHeboEspeces = !isMethodNotCash(paymentMethodHeboId);
  const isRestoEspeces = !isMethodNotCash(paymentMethodRestoId);
  const isExtraEspeces = !isMethodNotCash(paymentMethodExtraId);

  const handleSubmitHebbergement = async (e, isRemboursement = false) => {
    if (e) e.preventDefault();

    if (isLateCheckout && !lateForm.regle_tarif_id) {
      showToast("Veuillez sélectionner une grille tarifaire pour le Late Check-out", "error");
      return;
    }

    if (!isHeboEspeces && !referenceHebo.trim()) {
      showToast("La référence de paiement est obligatoire pour l'hébergement", "error");
      return;
    }

    if (resteAPayerResto > 0.1) {
      showToast("Impossible de clôturer : la facture de restauration doit être entièrement soldée au préalable.", "error");
      return;
    }

    if (resteAPayerExtras > 0.1) {
      showToast("Impossible de clôturer : les consommations d'extras doivent être entièrement soldées au préalable.", "error");
      return;
    }

    const montantSaisiUser = isRemboursement ? 0 : Number(montantHeboVerse !== '' ? montantHeboVerse : resteAPayerHebo);
    
    if (!isRemboursement && montantSaisiUser > resteAPayerHebo + 1) {
      showToast(`Le montant hébergement (${montantSaisiUser.toLocaleString()} F) ne peut pas dépasser le reste à payer hébergement (${resteAPayerHebo.toLocaleString()} F) !`, "error");
      return;
    }

    setLoading(true);
    try {
      const selectedMethodObj = methodesPaiement.find(m => m.id === paymentMethodHeboId);
      const payload = {
        customer_id: customerId,
        payment_method_id: paymentMethodHeboId || methodesPaiement[0]?.id || '',
        mode_paiement: selectedMethodObj?.libelle || 'ESPECES',
        reference: referenceHebo.trim() || null,
        montant_verse: montantSaisiUser,
        montant_hebergement: montantSaisiUser,
        montant_restauration: 0,
        montant_extras: 0,
        montant_ht: Math.round(totalHT),
        montant_tva: Math.round(totalTVA),
        est_depart_anticipe: mode === 'depart_anticipe',
        is_late_checkout: isLateCheckout,
        late_form: isLateCheckout ? lateForm : null
      };

      const isGroupe = Boolean(reservationId);
      let endpoint = '';

      if (estDejaCheckout) {
        endpoint = isGroupe 
          ? `/hebergement/reservations/${reservationId}/paiement-avance` 
          : `/hebergement/reservations/lignes-chambres/${ligneId}/paiement-avance`;
      } else if (mode === 'facture' || mode === 'facture_groupe') {
        endpoint = isGroupe 
          ? `/hebergement/reservations/${reservationId}/paiement-avance` 
          : `/hebergement/reservations/lignes-chambres/${ligneId}/paiement-avance`;
      } else {
        endpoint = isGroupe 
          ? `/hebergement/reservations/${reservationId}/checkout` 
          : `/hebergement/reservations/lignes-chambres/${ligneId}/checkout`;
      }

      await API.post(endpoint, payload).catch(async (err) => {
        if (mode === 'facture' || mode === 'facture_groupe' || estDejaCheckout) {
          const fallbackEp = isGroupe 
            ? `/hebergement/reservations/${reservationId}/paiement` 
            : `/hebergement/reservations/lignes-chambres/${ligneId}/paiement`;
          await API.post(fallbackEp, payload);
        } else {
          throw err;
        }
      });

      showToast("Paiement hébergement enregistré avec succès !");
      
      await fetchCheckoutDetails();
      setMontantHeboVerse('');
      setReferenceHebo('');

      if (!estDejaCheckout && (mode === 'depart_anticipe' || (!mode || mode === ''))) {
        setTimeout(() => {
          handlePrint();
          if (mode !== 'depart_anticipe') {
            navigate('/admin/hebergement/checkin-checkout');
          }
        }, 500);
      }
    } catch (err) {
      console.error("Erreur traitement hébergement:", err);
      showToast(err.response?.data?.error || err.response?.data?.message || "Erreur lors de la validation hébergement", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckoutDirect = async () => {
    if (resteAPayerResto > 0.1) {
      showToast("Veuillez solder la facture de restauration avant de procéder à la clôture.", "error");
      return;
    }
    if (resteAPayerExtras > 0.1) {
      showToast("Veuillez solder les consommations d'extras avant de procéder à la clôture.", "error");
      return;
    }

    setLoading(true);
    try {
      const activeMethodId = paymentMethodHeboId || (methodesPaiement.length > 0 ? methodesPaiement[0].id : '');
      const selectedMethodObj = methodesPaiement.find(m => m.id === activeMethodId);

      const payload = {
        customer_id: customerId,
        payment_method_id: activeMethodId,
        mode_paiement: selectedMethodObj?.libelle || 'ESPECES',
        reference: referenceHebo.trim() || null,
        montant_verse: Number(resteAPayerHebo || 0),
        montant_hebergement: Number(resteAPayerHebo || 0),
        montant_restauration: 0,
        montant_extras: 0,
        montant_ht: Math.round(totalHT),
        montant_tva: Math.round(totalTVA),
        is_late_checkout: false,
        late_form: null
      };

      const isGroupe = Boolean(reservationId);
      const checkoutEndpoint = isGroupe 
        ? `/hebergement/reservations/${reservationId}/checkout` 
        : `/hebergement/reservations/lignes-chambres/${ligneId}/checkout`;

      await API.post(checkoutEndpoint, payload);
      showToast("Check-out validé avec succès !");
      
      await fetchCheckoutDetails();
      setTimeout(() => {
        handlePrint();
        navigate('/admin/hebergement/checkin-checkout');
      }, 600);
    } catch (err) {
      console.error("Erreur check-out direct:", err);
      showToast(err.response?.data?.error || err.response?.data?.message || "Erreur lors du check-out", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRestauration = async (e) => {
    if (e) e.preventDefault();

    if (!isRestoEspeces && !referenceResto.trim()) {
      showToast("La référence de paiement est obligatoire pour la restauration", "error");
      return;
    }

    const montantSaisiResto = Number(montantRestoVerse !== '' ? montantRestoVerse : resteAPayerResto);
    
    if (montantSaisiResto > resteAPayerResto + 1) {
      showToast(`Le montant restauration (${montantSaisiResto.toLocaleString()} F) ne peut pas dépasser le reste à payer restauration (${resteAPayerResto.toLocaleString()} F) !`, "error");
      return;
    }

    setLoading(true);
    try {
      const isGroupe = Boolean(reservationId);
      const selectedMethodObj = methodesPaiement.find(m => m.id === paymentMethodRestoId);

      const payload = {
        customer_id: customerId,
        payment_method_id: paymentMethodRestoId,
        mode_paiement: selectedMethodObj?.libelle || 'ESPECES',
        reference: referenceResto.trim() || null,
        montant_verse: montantSaisiResto,
        montant_hebergement: 0,
        montant_restauration: montantSaisiResto,
      };

      const restoEndpoint = isGroupe 
        ? `/hebergement/reservations/${reservationId}/paiement-restauration` 
        : `/hebergement/reservations/lignes-chambres/${ligneId}/paiement-restauration`;

      await API.post(restoEndpoint, payload).catch(async () => {
        const fallbackRestoEp = isGroupe 
          ? `/hebergement/reservations/${reservationId}/paiement` 
          : `/hebergement/reservations/lignes-chambres/${ligneId}/paiement`;
        await API.post(fallbackRestoEp, payload);
      });

      showToast("Règlement de la restauration enregistré avec succès !");
      await fetchCheckoutDetails();
      setMontantRestoVerse('');
      setReferenceResto('');
    } catch (err) {
      console.error("Erreur traitement restauration:", err);
      showToast(err.response?.data?.error || "Erreur lors du règlement de la restauration", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitExtras = async (e) => {
    if (e) e.preventDefault();

    if (!isExtraEspeces && !referenceExtra.trim()) {
      showToast("La référence de paiement est obligatoire pour les extras", "error");
      return;
    }

    const montantSaisiExtras = Number(montantExtraVerse !== '' ? montantExtraVerse : resteAPayerExtras);
    
    if (montantSaisiExtras > resteAPayerExtras + 1) {
      showToast(`Le montant extras (${montantSaisiExtras.toLocaleString()} F) ne peut pas dépasser le reste à payer extras (${resteAPayerExtras.toLocaleString()} F) !`, "error");
      return;
    }

    setLoading(true);
    try {
      const selectedMethodObj = methodesPaiement.find(m => m.id === paymentMethodExtraId);

      for (const ext of consommationsExtras) {
        const payeExt = Number(ext.montant_paye || 0);
        const totalExt = Number(ext.montant_ttc || ext.montant || 0);
        const resteExt = Math.max(0, totalExt - payeExt);

        if (resteExt > 0.1 && montantSaisiExtras > 0) {
          const aPayerCetteLigne = Math.min(montantSaisiExtras, resteExt);
          
          await API.post(`/hebergement/reservations/extras/consommations/${ext.id}/payer`, {
            montant_verse: aPayerCetteLigne,
            montant: aPayerCetteLigne,
            payment_method_id: paymentMethodExtraId,
            moyen_paiement: selectedMethodObj?.libelle || 'ESPECES',
            mode_paiement: selectedMethodObj?.libelle || 'ESPECES',
            reference: referenceExtra.trim() || null,
            observation: 'Règlement groupé checkout'
          });
          break; 
        }
      }

      showToast("Règlement des prestations extras enregistré avec succès !");
      setMontantExtraVerse('');
      setReferenceExtra('');
      await fetchCheckoutDetails();
    } catch (err) {
      console.error("Erreur traitement extras:", err);
      showToast(err.response?.data?.error || err.response?.data?.message || "Erreur lors du règlement des extras", "error");
    } finally {
      setLoading(false);
    }
  };

  let titrePage = "FACTURATION & CLÔTURE DE SÉJOUR";
  let couleurTheme = "#dc2626";
  let texteBouton = "Valider le Check-out & Libérer";

  if (mode === 'facture' || mode === 'facture_groupe') {
    titrePage = "FACTURATION ANTICIPÉE & ENCAISSEMENT D'AVANCE";
    couleurTheme = "#2563eb";
    texteBouton = "Enregistrer l'Encaissement (Laisser en chambre)";
  } else if (mode === 'depart_anticipe') {
    titrePage = "GESTION DU DÉPART ANTICIPÉ & CLÔTURE";
    couleurTheme = "#d97706";
    texteBouton = "Valider le Départ Anticipé & Libérer";
  }

  const toutesLignesRestauration = [];
  let restoHTTotal = 0;
  let restoTVATotal = 0;

  facturesRestoPostees.forEach(facture => {
    const itemsVente = facture.items || facture.lignes || [];
    const restoSoumisTva = isEntrepriseSousTva && (facture.soumis_tva === 1 || facture.soumis_tva === true);

    if (itemsVente.length > 0) {
      itemsVente.forEach(item => {
        const itemTTC = Number(item.montant_ttc_ligne || item.montant_total || item.total || 0);
        const itemSoumis = restoSoumisTva && (item.soumis_tva === 1 || item.soumis_tva === true);
        const itemTaux = Number(item.taux_tva || companyInfo.taux_tva);

        let itemHT = 0;
        let itemTVA = 0;

        if (itemSoumis && itemTaux > 0) {
          itemHT = itemTTC / (1 + (itemTaux / 100));
          itemTVA = itemTTC - itemHT;
        } else {
          itemHT = itemTTC;
          itemTVA = 0;
        }

        restoHTTotal += itemHT;
        restoTVATotal += itemTVA;

        toutesLignesRestauration.push({
          designation: item.designation || item.nom_article || item.article_nom || "Consommation Bar / Restaurant",
          quantite: item.quantite || item.qte || 1,
          prix_unitaire: Number(item.prix_unitaire || item.pu || 0),
          montant_ttc: itemTTC,
          montant_ht: itemHT,
          montant_tva: itemTVA
        });
      });
    } else {
      const factureTTC = Number(facture.montant_total || facture.montant || 0);
      let factureHT = 0;
      let factureTVA = 0;

      if (restoSoumisTva && companyInfo.taux_tva > 0) {
        factureHT = factureTTC / (1 + (companyInfo.taux_tva / 100));
        factureTVA = factureTTC - factureHT;
      } else {
        factureHT = factureTTC;
        factureTVA = 0;
      }

      restoHTTotal += factureHT;
      restoTVATotal += factureTVA;

      toutesLignesRestauration.push({
        designation: facture.designation || facture.reference || "Consommation Bar / Restaurant",
        quantite: 1,
        prix_unitaire: factureTTC,
        montant_ttc: factureTTC,
        montant_ht: factureHT,
        montant_tva: factureTVA
      });
    }
  });

  const toutesLignesExtras = consommationsExtras.map(ext => {
    const extTTC = Number(ext.montant_ttc || ext.montant || 0);
    const extHT = Number(ext.montant_ht || extTTC);
    const extTVA = Number(ext.montant_tva || 0);

    return {
      designation: `Prestation Extra - ${ext.nom_service || 'Service Extra'}`,
      quantite: ext.quantite || 1,
      prix_unitaire: Number(ext.prix_unitaire || 0),
      montant_ttc: extTTC,
      montant_ht: extHT,
      montant_tva: extTVA
    };
  });

  const hebergementTTC = montantNuitees + montantEarly + montantLate;
  let hebergementHT = 0;
  let hebergementTVA = 0;

  const premierSoumisTva = isEntrepriseSousTva && (lignesTarifiees[0]?.soumis_tva === 1 || lignesTarifiees[0]?.soumis_tva === true || detailsSejour?.soumis_tva === 1);
  const tauxTvaHébergement = Number(lignesTarifiees[0]?.taux_tva || detailsSejour?.taux_tva || companyInfo.taux_tva);

  if (premierSoumisTva && tauxTvaHébergement > 0) {
    hebergementHT = hebergementTTC / (1 + (tauxTvaHébergement / 100));
    hebergementTVA = hebergementTTC - hebergementHT;
  } else {
    hebergementHT = hebergementTTC;
    hebergementTVA = 0;
  }

  const finalRestoHT = Number(detailsSejour?.montant_restauration_ht || restoHTTotal);
  const finalRestoTVA = Number(detailsSejour?.montant_restauration_tva || restoTVATotal);

  const finalExtrasHT = Number(detailsSejour?.montant_extras_ht || consommationsExtras.reduce((acc, e) => acc + Number(e.montant_ht || 0), 0));
  const finalExtrasTVA = Number(detailsSejour?.montant_extras_tva || consommationsExtras.reduce((acc, e) => acc + Number(e.montant_tva || 0), 0));

  const finalGlobalHT = Number(detailsSejour?.montant_ht || (hebergementHT + finalRestoHT + finalExtrasHT));
  const finalGlobalTVA = Number(detailsSejour?.montant_tva || (hebergementTVA + finalRestoTVA + finalExtrasTVA));

  const estFactureSoldee = (resteAPayerHebo <= 0.1 && resteAPayerResto <= 0.1 && resteAPayerExtras <= 0.1) || 
                          (detailsSejour?.is_solde === 1) || 
                          (Number(montantHeboVerse) >= resteAPayerHebo && resteAPayerResto <= 0.1 && resteAPayerExtras <= 0.1);

  const facturePrintData = {
    facture_no: detailsSejour?.facture_no || detailsSejour?.numero || reservationId || ligneId,
    date: detailsSejour?.created_at || new Date().toISOString(),
    est_solde: estFactureSoldee,
    montant_ht: Number(finalGlobalHT.toFixed(2)),
    montant_tva: Number(finalGlobalTVA.toFixed(2)),
    montant_ttc: montantTotalGlobal,
    is_sous_tva: isEntrepriseSousTva,
    taux_tva: companyInfo.taux_tva,
    client: societeDetails ? {
      nom_complet: societeDetails.nom || societeDetails.raison_sociale,
      nif: societeDetails.nif || societeDetails.ifu || "0",
      adresse: societeDetails.adresse || "Non renseignée",
      telephone: societeDetails.telephone || "Non renseigné",
      email: societeDetails.email || "",
      client_occupant: clientNom
    } : {
      nom_complet: clientNom,
      nif: clientObjDetails.nif || detailsSejour?.client_nif || detailsSejour?.nif || "0",
      adresse: clientObjDetails.adresse || detailsSejour?.client_adresse || detailsSejour?.adresse || "Non renseignée",
      telephone: clientObjDetails.telephone || detailsSejour?.client_telephone || detailsSejour?.telephone || "Non renseigné",
      email: clientObjDetails.email || detailsSejour?.client_email || detailsSejour?.email || ""
    },
    lignes_chambres: lignesTarifiees.map(l => {
      const ligneSoumise = isEntrepriseSousTva && (l.soumis_tva === 1 || l.soumis_tva === true || detailsSejour?.soumis_tva === 1);
      const lTaux = Number(l.taux_tva || detailsSejour?.taux_tva || companyInfo.taux_tva);
      const lHt = ligneSoumise && lTaux > 0 ? l.montant_ttc / (1 + (lTaux / 100)) : l.montant_ttc;
      const lTva = ligneSoumise && lTaux > 0 ? l.montant_ttc - lHt : 0;

      return {
        designation: societeDetails 
          ? `Hébergement - Chambre ${l.chambre} (Occupant : ${l.occupant_nom})`
          : `Hébergement - Chambre ${l.chambre} (${l.occupant_nom})`,
        chambre_nom: l.chambre,
        date_arrivee: l.date_check_in || dateArriveeStr,
        date_depart: l.date_check_out || nouvelleDateDepart || dateDepartStr,
        nombre_nuitees: l.nuites,
        prix_unitaire: l.prix_unitaire,
        remise: 0,
        montant_total: l.montant_ttc,
        montant_ht: Number(lHt.toFixed(2)),
        montant_tva: Number(lTva.toFixed(2))
      };
    }),
    hebergement: {
      designation: societeDetails 
        ? (lignesTarifiees.length > 1 ? `Hébergement Groupe (${lignesTarifiees.length} chambres) - Occupant principal : ${clientNom}` : `Hébergement - Chambre ${lignesTarifiees[0]?.chambre || 'N/A'} (Occupant : ${clientNom})`)
        : (lignesTarifiees.length > 1 ? `Hébergement Groupe (${lignesTarifiees.length} chambres)` : (lignesTarifiees.length === 1 ? `Hébergement - Chambre ${lignesTarifiees[0].chambre}` : "Séjour hôtelier")),
      chambre_nom: lignesTarifiees.length === 1 ? lignesTarifiees[0].chambre : 'Groupe',
      date_arrivee: dateArriveeStr,
      date_depart: nouvelleDateDepart || dateDepartStr,
      nombre_nuites: totalNuitesGroupe,
      prix_unitaire: lignesTarifiees.length === 1 ? lignesTarifiees[0].prix_unitaire : hebergementTTC,
      remise: 0,
      montant_total: hebergementTTC,
      montant_ht: Number(hebergementHT.toFixed(2)),
      montant_tva: Number(hebergementTVA.toFixed(2))
    },
    restauration: toutesLignesRestauration,
    montant_restauration_ht: Number(finalRestoHT.toFixed(2)),
    montant_restauration_tva: Number(finalRestoTVA.toFixed(2)),
    extras: toutesLignesExtras,
    montant_extras_ht: Number(finalExtrasHT.toFixed(2)),
    montant_extras_tva: Number(finalExtrasTVA.toFixed(2))
  };

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#f8fafc' }}>
      <Sidebar />
      <main style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
         
        {toast.show && (
          <div style={{...toastContainerStyle, background: toast.type === 'success' ? '#10b981' : '#ef4444'}}>
            {toast.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>}
            {toast.message}
          </div>
        )}

        <header style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <button type="button" onClick={() => navigate(-1)} style={backBtnStyle}>
              <ArrowLeft size={14} /> Retour
            </button>
            <h1 style={{ margin: 0, fontSize: '16px', fontWeight: '900', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <LogOut size={18} color={couleurTheme} /> {titrePage}
            </h1>
          </div>
        </header>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 30px', boxSizing: 'border-box', maxWidth: '1300px', margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column' }}>
            
          {loading && !detailsSejour ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Chargement des données de facturation...</div>
          ) : (
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px rgba(0,0,0,0.02)', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '12px' }}>
                <div>
                  <h2 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: '900', color: '#1e293b' }}>
                    Note de Frais & Réservation #{reservationId || ligneId}
                  </h2>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Gestion financière séparée (Hébergement, Restauration & Prestations Extras)</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '12px', fontWeight: '800', color: estDejaCheckout ? '#059669' : couleurTheme }}>
                    {estDejaCheckout 
                      ? 'STATUT : CHECK-OUT DÉJÀ EFFECTUÉ (CHAMBRE(S) LIBÉRÉE(S))' 
                      : (estFactureSoldee ? 'STATUT : FACTURE TOTALEMENT SOLDÉE' : 'STATUT : ACTIF EN CHAMBRE')}
                  </div>
                  <div style={{ fontSize: '11px', color: '#64748b' }}>Date du jour : {new Date().toLocaleDateString()}</div>
                </div>
              </div>

              {societeDetails && (
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', padding: '12px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Building2 size={20} color="#1d4ed8" />
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: '900', color: '#1e40af', textTransform: 'uppercase' }}>Facturation rattachée à la société partenaire (Facture à son nom) :</div>
                      <div style={{ fontSize: '13px', fontWeight: '900', color: '#1e3a8a' }}>{societeDetails.nom || societeDetails.raison_sociale} {societeDetails.nif ? `(NIF: ${societeDetails.nif})` : ''}</div>
                      <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>Client occupant hébergé : <strong>{clientNom}</strong></div>
                    </div>
                  </div>
                  <div style={{ fontSize: '11px', color: '#3b82f6', fontWeight: '800' }}>
                    Compte Entreprise
                  </div>
                </div>
              )}

              {mode === 'depart_anticipe' && !estDejaCheckout && (
                <div style={{ background: '#fffbeb', padding: '12px 16px', borderRadius: '8px', border: '1px solid #fde68a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: '900', color: '#b45309', textTransform: 'uppercase' }}>
                      Date de départ effective (Anticipé) :
                    </label>
                  </div>
                  <input 
                    type="date"
                    style={{ ...inputStyle, width: '180px', marginTop: '0', fontWeight: 'bold' }}
                    min={dateArriveeClean}
                    value={nouvelleDateDepart}
                    onChange={(e) => setNouvelleDateDepart(e.target.value)}
                    required
                  />
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: societeDetails ? '1fr 1fr 1fr' : '1fr 1fr', gap: '20px' }}>
                {societeDetails && (
                  <div style={{ background: '#eff6ff', padding: '14px 18px', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                    <div style={{ ...sectionTitleStyle, color: '#1d4ed8' }}><Building2 size={14}/> Société Facturée (Payeur)</div>
                    <div style={{ fontSize: '13px', fontWeight: '900', color: '#1e3a8a', marginTop: '6px' }}>{societeDetails.nom || societeDetails.raison_sociale}</div>
                    <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>Tél : {societeDetails.telephone || 'Non renseigné'}</div>
                    <div style={{ fontSize: '12px', color: '#475569' }}>E-mail : {societeDetails.email || 'Non renseigné'}</div>
                    <div style={{ fontSize: '12px', color: '#475569' }}>NIF : {societeDetails.nif || societeDetails.ifu || '0'}</div>
                    <div style={{ fontSize: '12px', color: '#475569' }}>Adresse : {societeDetails.adresse || 'Non renseignée'}</div>
                  </div>
                )}

                <div style={{ background: '#f8fafc', padding: '14px 18px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={sectionTitleStyle}><User size={14}/> {societeDetails ? 'Client / Occupant' : 'Informations Client'}</div>
                  <div style={{ fontSize: '13px', fontWeight: '800', color: '#1e293b', marginTop: '6px' }}>{clientNom}</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Tél : {clientObjDetails.telephone || detailsSejour?.client_telephone || 'Non renseigné'}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>E-mail : {clientObjDetails.email || detailsSejour?.client_email || 'Non renseigné'}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>NIF : {clientObjDetails.nif || detailsSejour?.client_nif || detailsSejour?.nif || '0'}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Adresse : {clientObjDetails.adresse || detailsSejour?.client_adresse || detailsSejour?.adresse || 'Non renseignée'}</div>
                </div>

                <div style={{ background: '#f8fafc', padding: '14px 18px', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={sectionTitleStyle}><Bed size={14}/> Détails du Logement</div>
                    <div style={{ fontSize: '13px', fontWeight: '800', color: '#1e293b', marginTop: '6px' }}>
                      {lignesTarifiees.length > 1 ? `Réservation Groupe (${lignesTarifiees.length} chambres)` : (detailsSejour?.chambre_numero ? `Chambre N° ${detailsSejour.chambre_numero}` : `Séjour / Groupe`)}
                    </div>
                  </div>
                  <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', padding: '8px 14px', borderRadius: '8px', textAlign: 'center', minWidth: '85px' }}>
                    <div style={{ fontSize: '9px', fontWeight: '900', color: '#1d4ed8', textTransform: 'uppercase' }}>Total Nuitées</div>
                    <div style={{ fontSize: '15px', fontWeight: '900', color: '#2563eb', marginTop: '2px' }}>
                      {totalNuitesGroupe} nuit{totalNuitesGroupe > 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 style={{ fontSize: '12px', fontWeight: '900', color: '#475569', textTransform: 'uppercase', marginBottom: '8px' }}>Synthèse Financière Détaillée</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#64748b', textAlign: 'left' }}>
                      <th style={{ padding: '8px 10px' }}>Description & Détails</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right' }}>Montant TTC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lignesTarifiees.map((ligne, idx) => {
                      const montantPayeLigne = Number(ligne.montant_paye || 0);
                      const resteLigne = Number(ligne.reste_a_payer !== undefined ? ligne.reste_a_payer : (ligne.montant_ttc - montantPayeLigne));
                      const estSoldeLigne = resteLigne <= 0.1;

                      return (
                        <tr key={ligne.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px 10px', fontWeight: '700', color: '#1e293b' }}>
                            👤 <strong>{ligne.occupant_nom}</strong> — Chambre N° <strong>{ligne.chambre}</strong> ({ligne.nuites} nuit(s) × {Number(ligne.prix_unitaire).toLocaleString()} F)
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal', marginTop: '2px' }}>
                              Réglé : <span style={{ color: '#059669', fontWeight: 'bold' }}>{montantPayeLigne.toLocaleString()} F</span> | 
                              Reste : <span style={{ color: estSoldeLigne ? '#059669' : '#be123c', fontWeight: 'bold' }}>{resteLigne.toLocaleString()} F</span>
                              {estSoldeLigne && <span style={{ marginLeft: '8px', background: '#ecfdf5', color: '#065f46', padding: '1px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: '900' }}>SOLDÉ</span>}
                            </div>
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '800', color: '#334155' }}>
                            {Number(ligne.montant_ttc).toLocaleString()} F
                          </td>
                        </tr>
                      );
                    })}

                    {hasResto && (
                      <tr style={{ background: '#fff1f2', borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 10px', fontWeight: '700', color: '#be123c' }}>
                          <Utensils size={14}/> Total Restauration affectée ({facturesRestoPostees.length} bon(s))
                          <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal', marginTop: '2px' }}>
                            Déjà payé : <span style={{ color: '#059669', fontWeight: 'bold' }}>{totalPaiementsResto.toLocaleString()} F</span> | Reste : <span style={{ color: '#be123c', fontWeight: 'bold' }}>{resteAPayerResto.toLocaleString()} F</span>
                          </div>
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '800', color: '#be123c' }}>
                          +{Number(montantTotalResto).toLocaleString()} F
                        </td>
                      </tr>
                    )}

                    {hasExtras && (
                      <tr style={{ background: '#f5f3ff', borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 10px', fontWeight: '700', color: '#7c3aed' }}>
                          <ShoppingBag size={14}/> Total Prestations Extras affectées ({consommationsExtras.length} prestation(s))
                          <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal', marginTop: '2px' }}>
                            Déjà payé : <span style={{ color: '#059669', fontWeight: 'bold' }}>{totalPaiementsExtras.toLocaleString()} F</span> | Reste : <span style={{ color: '#7c3aed', fontWeight: 'bold' }}>{resteAPayerExtras.toLocaleString()} F</span>
                          </div>
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '800', color: '#7c3aed' }}>
                          +{Number(montantTotalExtras).toLocaleString()} F
                        </td>
                      </tr>
                    )}

                    <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                      <td style={{ padding: '10px', fontWeight: '900', color: '#1e293b', fontSize: '13px' }}>MONTANT GLOBAL DU SÉJOUR</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: '900', color: '#059669', fontSize: '14px' }}>{Number(montantTotalGlobal).toLocaleString()} F CFA</td>
                    </tr>

                    <tr style={{ background: '#ecfdf5', borderTop: '1px solid #a7f3d0' }}>
                      <td style={{ padding: '10px', fontWeight: '900', color: '#065f46', fontSize: '13px' }}>TOTAL PAYÉ GLOBAL</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: '900', color: '#059669', fontSize: '14px' }}>{Number(totalPayeHeboGlobal + totalPaiementsResto + totalPaiementsExtras).toLocaleString()} F CFA</td>
                    </tr>

                    <tr style={{ background: '#fef2f2', borderTop: '1px solid #fecaca' }}>
                      <td style={{ padding: '10px', fontWeight: '900', color: '#991b1b', fontSize: '13px' }}>RESTE À PAYER GLOBAL</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: '900', color: '#be123c', fontSize: '14px' }}>{Number(resteAPayerHebo + resteAPayerResto + resteAPayerExtras).toLocaleString()} F CFA</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {hasResto && resteAPayerResto > 0.1 && (
                <form onSubmit={handleSubmitRestauration} style={{ background: '#fff1f2', padding: '16px 20px', borderRadius: '10px', border: '1px solid #fecdd3', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '13px', fontWeight: '900', color: '#be123c', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Utensils size={16}/> Règlement Facture Restauration (Reste à payer : {resteAPayerResto.toLocaleString()} F)
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: isRestoEspeces ? '1fr 1fr' : '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={labelStyle}>MODE DE PAIEMENT (RESTAURATION)</label>
                      <select 
                        style={inputStyle}
                        value={paymentMethodRestoId}
                        onChange={(e) => setPaymentMethodRestoId(e.target.value)}
                        required
                      >
                        <option value="">-- Sélectionner un mode --</option>
                        {methodesPaiement.map(methode => (
                          <option key={methode.id} value={methode.id}>{methode.libelle}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>MONTANT VERSÉ (RESTAURATION)</label>
                      <input 
                        type="number" 
                        max={resteAPayerResto}
                        style={inputStyle}
                        value={montantRestoVerse}
                        onChange={(e) => setMontantRestoVerse(e.target.value)}
                        placeholder={`Max: ${resteAPayerResto}`}
                        required
                      />
                    </div>
                    {!isRestoEspeces && (
                      <div>
                        <label style={labelStyle}>RÉFÉRENCE DE PAIEMENT *</label>
                        <input 
                          type="text"
                          style={inputStyle}
                          value={referenceResto}
                          onChange={(e) => setReferenceResto(e.target.value)}
                          placeholder="N° de chèque, transaction..."
                          required
                        />
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px' }}>
                    <button 
                      type="submit" 
                      disabled={loading} 
                      style={{ padding: '10px 20px', background: '#be123c', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', fontSize: '12px' }}
                    >
                      {loading ? 'Traitement...' : "Valider le règlement Restauration"}
                    </button>
                  </div>
                </form>
              )}

              {hasResto && resteAPayerResto <= 0.1 && (
                <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '12px 16px', borderRadius: '8px', color: '#065f46', fontWeight: '800', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle size={16} /> Le règlement de la restauration est totalement soldé (Total réglé : {totalPaiementsResto.toLocaleString()} F).
                </div>
              )}

              {hasExtras && resteAPayerExtras > 0.1 && (
                <form onSubmit={handleSubmitExtras} style={{ background: '#f5f3ff', padding: '16px 20px', borderRadius: '10px', border: '1px solid #ddd6fe', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '13px', fontWeight: '900', color: '#7c3aed', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <ShoppingBag size={16}/> Règlement Prestations Extras (Reste à payer : {resteAPayerExtras.toLocaleString()} F)
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: isExtraEspeces ? '1fr 1fr' : '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={labelStyle}>MODE DE PAIEMENT (EXTRAS)</label>
                      <select 
                        style={inputStyle}
                        value={paymentMethodExtraId}
                        onChange={(e) => setPaymentMethodExtraId(e.target.value)}
                        required
                      >
                        <option value="">-- Sélectionner un mode --</option>
                        {methodesPaiement.map(methode => (
                          <option key={methode.id} value={methode.id}>{methode.libelle}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>MONTANT VERSÉ (EXTRAS)</label>
                      <input 
                        type="number" 
                        max={resteAPayerExtras}
                        style={inputStyle}
                        value={montantExtraVerse}
                        onChange={(e) => setMontantExtraVerse(e.target.value)}
                        placeholder={`Max: ${resteAPayerExtras}`}
                        required
                      />
                    </div>
                    {!isExtraEspeces && (
                      <div>
                        <label style={labelStyle}>RÉFÉRENCE DE PAIEMENT *</label>
                        <input 
                          type="text"
                          style={inputStyle}
                          value={referenceExtra}
                          onChange={(e) => setReferenceExtra(e.target.value)}
                          placeholder="N° de chèque, transaction..."
                          required
                        />
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px' }}>
                    <button 
                      type="submit" 
                      disabled={loading} 
                      style={{ padding: '10px 20px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', fontSize: '12px' }}
                    >
                      {loading ? 'Traitement...' : "Valider le règlement Extras"}
                    </button>
                  </div>
                </form>
              )}

              {hasExtras && resteAPayerExtras <= 0.1 && (
                <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '12px 16px', borderRadius: '8px', color: '#065f46', fontWeight: '800', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle size={16} /> Le règlement des prestations extras est totalement soldé (Total réglé : {totalPaiementsExtras.toLocaleString()} F).
                </div>
              )}

              {resteAPayerHebo > 0.1 ? (
                <form onSubmit={(e) => handleSubmitHebbergement(e, false)} style={{ background: '#fef2f2', padding: '16px 20px', borderRadius: '10px', border: '1px solid #fecaca', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '13px', fontWeight: '900', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Bed size={16}/> Règlement Solde / Facture Hébergement (Reste à payer : {resteAPayerHebo.toLocaleString()} F)
                  </h3>

                  {!estDejaCheckout && mode !== 'facture' && mode !== 'facture_groupe' && (
                    <div style={{ background: 'white', border: '1px solid #e2e8f0', padding: '10px 14px', borderRadius: '8px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontWeight: '800', fontSize: '12px', color: '#92400e' }}>
                        <input 
                          type="checkbox" 
                          checked={isLateCheckout}
                          onChange={(e) => setIsLateCheckout(e.target.checked)}
                          style={{ width: '16px', height: '16px', accentColor: '#d97706', cursor: 'pointer' }}
                        />
                        <Clock size={14} /> Le client a effectué un Late Check-out (Départ tardif)
                      </label>

                      {isLateCheckout && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '10px', paddingTop: '8px', borderTop: '1px dashed #fcd34d' }}>
                          <div>
                            <label style={labelStyle}>Heure effective</label>
                            <input 
                              type="time" 
                              style={inputStyle}
                              value={lateForm.heure_depart_effective}
                              onChange={(e) => setLateForm({...lateForm, heure_depart_effective: e.target.value})}
                              required={isLateCheckout}
                            />
                          </div>
                          <div>
                            <label style={labelStyle}>Grille tarifaire</label>
                            <select 
                              style={inputStyle}
                              value={lateForm.regle_tarif_id}
                              onChange={(e) => setLateForm({...lateForm, regle_tarif_id: e.target.value})}
                              required={isLateCheckout}
                            >
                              <option value="">-- Sélectionner --</option>
                              {reglesTarifs.map(r => (
                                <option key={r.id} value={r.id}>
                                  {r.libelle} ({r.mode_valeur === 'FIXE' ? `${r.montant} F` : `${r.montant}%`})
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: isHeboEspeces ? '1fr 1fr' : '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={labelStyle}>MODE DE PAIEMENT (HÉBERGEMENT)</label>
                      <select 
                        style={inputStyle}
                        value={paymentMethodHeboId}
                        onChange={(e) => setPaymentMethodHeboId(e.target.value)}
                        required
                      >
                        <option value="">-- Sélectionner un mode --</option>
                        {methodesPaiement.map(methode => (
                          <option key={methode.id} value={methode.id}>{methode.libelle}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>MONTANT VERSÉ (HÉBERGEMENT)</label>
                      <input 
                        type="number" 
                        max={resteAPayerHebo}
                        style={inputStyle}
                        value={montantHeboVerse}
                        onChange={(e) => setMontantHeboVerse(e.target.value)}
                        placeholder={`Max: ${resteAPayerHebo}`}
                        required
                      />
                    </div>
                    {!isHeboEspeces && (
                      <div>
                        <label style={labelStyle}>RÉFÉRENCE DE PAIEMENT *</label>
                        <input 
                          type="text"
                          style={inputStyle}
                          value={referenceHebo}
                          onChange={(e) => setReferenceHebo(e.target.value)}
                          placeholder="N° de chèque, transaction..."
                          required
                        />
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px' }}>
                    <button type="button" onClick={handlePrint} style={{ padding: '10px 16px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                      <Printer size={14}/> Imprimer Facture
                    </button>
                    {!((resteAPayerResto > 0.1 || resteAPayerExtras > 0.1) && mode !== 'facture' && mode !== 'facture_groupe') && (
                      <button 
                        type="submit" 
                        disabled={loading} 
                        style={{ padding: '10px 20px', background: estDejaCheckout ? '#059669' : couleurTheme, color: 'white', border: 'none', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', fontSize: '12px' }}
                      >
                        {loading ? 'Traitement...' : (estDejaCheckout ? 'Enregistrer le Règlement' : texteBouton)}
                      </button>
                    )}
                  </div>
                  {(resteAPayerResto > 0.1 || resteAPayerExtras > 0.1) && mode !== 'facture' && mode !== 'facture_groupe' && (
                    <div style={{ color: '#b91c1c', fontSize: '11px', fontWeight: '800', textAlign: 'right' }}>
                      ⚠ Veuillez solder la restauration et les extras ci-dessus pour pouvoir valider la clôture.
                    </div>
                  )}
                </form>
              ) : (
                <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '14px 18px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ color: '#065f46', fontWeight: '800', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircle size={16} /> Le règlement de l'hébergement est totalement soldé. {estDejaCheckout && "(Chambre(s) déjà libérée(s))"}
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button type="button" onClick={handlePrint} style={{ padding: '10px 16px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                      <Printer size={14}/> Imprimer Facture
                    </button>
                    {!estDejaCheckout && mode !== 'facture' && mode !== 'facture_groupe' && resteAPayerResto <= 0.1 && resteAPayerExtras <= 0.1 && (
                      <button 
                        type="button"
                        onClick={handleCheckoutDirect}
                        disabled={loading}
                        style={{ padding: '10px 20px', background: '#059669', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '900', cursor: 'pointer', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      >
                        <LogOut size={16} /> {loading ? 'Validation...' : (mode === 'depart_anticipe' ? 'Valider le Départ Anticipé & Libérer' : 'Valider le Check-out & Libérer')}
                      </button>
                    )}
                  </div>
                </div>
              )}

            </div>
          )}

        </div>
      </main>

      <div style={{ position: 'absolute', top: '-9999px', left: '-9999px', width: '210mm', opacity: '0', pointerEvents: 'none' }}>
        <FacturePrint 
          ref={printRef} 
          factureData={facturePrintData} 
          company={companyInfo} 
        />
      </div>

      <style>{`@keyframes slideInToast { from { transform: translate(-50%, -20px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }`}</style>
    </div>
  );
};

const headerStyle = { background: 'white', padding: '12px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', flexShrink: 0 };
const backBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#e2e8f0', border: 'none', padding: '6px 12px', borderRadius: '6px', fontWeight: '800', fontSize: '11px', cursor: 'pointer', color: '#334155' };
const sectionTitleStyle = { fontSize: '10px', fontWeight: '900', color: '#64748b', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px' };
const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '12px', marginTop: '4px', background: '#fff', boxSizing: 'border-box' };
const labelStyle = { fontSize: '10px', fontWeight: '900', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' };
const toastContainerStyle = { position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, color: 'white', padding: '10px 20px', borderRadius: '8px', boxShadow: '0 10px 15px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '800', fontSize: '12px', animation: 'slideInToast 0.3s ease-out' };

export default ChekoutFacturation;